import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useClient } from '../contexts/ClientContext';
import { Building2, Check, ChevronsUpDown, Search } from 'lucide-react';

export function ClientSwitcher() {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId?: string }>();
  const { clients, currentClient, setCurrentClient } = useClient();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Find current client from URL params
  useEffect(() => {
    if (clientId && clients.length > 0) {
      const client = clients.find(c => c.client_id === clientId);
      if (client && client.client_id !== currentClient?.client_id) {
        setCurrentClient(client);
      }
    }
  }, [clientId, clients, currentClient, setCurrentClient]);

  // Keyboard navigation handler (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Filter clients by search query
  const filteredClients = clients.filter(client =>
    client.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.client_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectClient = (client: typeof clients[0]) => {
    setCurrentClient(client);
    setIsOpen(false);
    setSearchQuery('');

    // Navigate to client detail, preserving current tab if on detail page
    if (clientId) {
      const currentTab = new URLSearchParams(window.location.search).get('tab');
      navigate(`/clients/${client.client_id}${currentTab ? `?tab=${currentTab}` : ''}`);
    } else {
      navigate(`/clients/${client.client_id}`);
    }
  };

  // If no clients yet, show loading state
  if (clients.length === 0) {
    return (
      <div
        className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400"
        role="status"
        aria-label="Loading client list"
      >
        <Building2 className="h-4 w-4" aria-hidden="true" />
        <span>Loading clients...</span>
      </div>
    );
  }

  // If only one client, show static display
  if (clients.length === 1) {
    return (
      <div
        className="flex items-center gap-2 px-3 py-2 text-sm text-slate-300"
        role="status"
        aria-label={`Current client: ${clients[0].client_name}`}
      >
        <Building2 className="h-4 w-4" aria-hidden="true" />
        <span className="font-medium">{clients[0].client_name}</span>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between gap-2 px-3 py-2 text-sm text-slate-300 hover:text-slate-100 hover:bg-slate-800/50 rounded-lg transition-colors min-w-[200px]"
        aria-label={currentClient ? `Current client: ${currentClient.client_name}. Press to switch clients` : 'Select a client'}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls="client-switcher-listbox"
      >
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4" aria-hidden="true" />
          <span className="font-medium truncate">
            {currentClient?.client_name || 'Select Client'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <kbd
            className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border border-slate-700 bg-slate-900 px-1.5 font-mono text-[10px] font-medium text-slate-400"
            aria-label="Keyboard shortcut: Command K"
          >
            <span className="text-xs">⌘</span>K
          </kbd>
          <ChevronsUpDown className="h-4 w-4 text-slate-500" aria-hidden="true" />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />

          {/* Dropdown Content */}
          <div
            id="client-switcher-listbox"
            className="absolute left-0 top-full mt-2 w-80 z-50 bg-slate-800 border border-slate-700 rounded-lg shadow-lg overflow-hidden"
            role="dialog"
            aria-label="Client selector"
          >
            {/* Search Input */}
            <div className="p-2 border-b border-slate-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" aria-hidden="true" />
                <input
                  type="text"
                  placeholder="Search clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-700 rounded-md text-sm text-slate-300 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                  aria-label="Search clients by name or email"
                  role="searchbox"
                />
              </div>
            </div>

            {/* Client List */}
            <div
              className="max-h-[300px] overflow-y-auto"
              role="listbox"
              aria-label="Available clients"
            >
              {filteredClients.length === 0 ? (
                <div
                  className="p-4 text-sm text-slate-500 text-center"
                  role="status"
                >
                  No clients found
                </div>
              ) : (
                filteredClients.map((client) => {
                  const isSelected = client.client_id === currentClient?.client_id;

                  return (
                    <button
                      key={client.client_id}
                      onClick={() => handleSelectClient(client)}
                      className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-slate-700/50 transition-colors ${
                        isSelected ? 'bg-slate-700/30' : ''
                      }`}
                      role="option"
                      aria-selected={isSelected}
                      aria-label={`${client.client_name}, ${client.client_email}${isSelected ? ' (currently selected)' : ''}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <Building2 className="h-4 w-4 text-slate-400 flex-shrink-0" aria-hidden="true" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-200 truncate">
                            {client.client_name}
                          </div>
                          <div className="text-xs text-slate-500 truncate">
                            {client.client_email}
                          </div>
                        </div>
                      </div>

                      {isSelected && (
                        <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" aria-hidden="true" />
                      )}
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer */}
            <div className="p-2 border-t border-slate-700 text-xs text-slate-500" role="status">
              <div className="flex items-center justify-between px-2 py-1">
                <span>{filteredClients.length} clients</span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-slate-900 rounded text-[10px]" aria-hidden="true">↑↓</kbd>
                  <span>to navigate</span>
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
